import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, CheckCircle2, MousePointerClick, Save } from 'lucide-react';
import { api } from '@/lib/api';
import { PageHeader, ErrorState } from '@/components/ui/misc';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Field, Input, Select } from '@/components/ui/input';
import { toast } from 'sonner';

interface LayoutItem { str: string; x: number; y: number; width: number; height: number }
interface LayoutPage { pageNumber: number; pageWidth: number; pageHeight: number; items: LayoutItem[] }
interface StoredAnchor { text: string; match: string; x: number; y: number; width?: number; height?: number; page?: number }
interface StoredTemplateConfig {
  version?: number;
  columns?: Partial<Record<ColumnKey, { x0: number; x1: number }>>;
  anchors?: Partial<Record<AnchorKey, StoredAnchor>>;
  headerTokens?: string[];
  pages?: Record<string, {
    columns?: Partial<Record<ColumnKey, { x0: number; x1: number }>>;
    headerTokens?: string[];
    headers?: Partial<Record<ColumnKey, StoredAnchor>>;
  }>;
}
interface StoredTemplate { id: string; name: string; isDefault: boolean; config: StoredTemplateConfig }
interface Layout {
  pageWidth: number; pageHeight: number; totalPages: number; items: LayoutItem[];
  pages: LayoutPage[];
  invoiceNumber: string;
  supplier: { id: string; name: string; vatNumber: string | null; invoiceTemplates: StoredTemplate[] };
}
interface SelectedToken { text: string; x: number; y: number; width: number; height: number; page: number }

type AnchorKey = 'vat' | 'supplierName' | 'tableHeader' | 'totalLabel';
type ColumnKey = 'description' | 'quantity' | 'unitCost' | 'discount' | 'taxRate' | 'lineTotal';
type FieldKey = AnchorKey | ColumnKey;

const ANCHORS: { key: AnchorKey; label: string; hint: string }[] = [
  { key: 'vat', label: '1 · CIF / NIF del proveedor', hint: 'Haz clic sobre el CIF o arrastra un rectángulo encima (p. ej. B12345678).' },
  { key: 'supplierName', label: '2 · Nombre del proveedor', hint: 'Haz clic sobre la razón social o arrastra encima de ella.' },
  { key: 'tableHeader', label: '3 · Cabecera de la tabla', hint: 'Haz clic sobre “Descripción” o “Concepto”, o arrastra sobre la cabecera.' },
  { key: 'totalLabel', label: '4 · Etiqueta de total', hint: 'Haz clic sobre “Total” o arrastra encima de la etiqueta.' }
];

const COLUMNS: { key: ColumnKey; label: string; required: boolean }[] = [
  { key: 'description', label: 'Descripción', required: true },
  { key: 'quantity', label: 'Cantidad', required: true },
  { key: 'unitCost', label: 'Precio unitario', required: true },
  { key: 'lineTotal', label: 'Importe de línea', required: true },
  { key: 'discount', label: 'Descuento (opcional)', required: false },
  { key: 'taxRate', label: 'IVA % (opcional)', required: false }
];

const FIELD_ORDER: FieldKey[] = ['vat', 'supplierName', 'tableHeader', 'totalLabel', 'description', 'quantity', 'unitCost', 'lineTotal', 'discount', 'taxRate'];

function anchorMatch(key: AnchorKey, text: string): string {
  const clean = text.trim();
  if (key === 'vat') {
    const vat = clean.match(/([A-Z]\s?\d{7}\s?[0-9A-J]|\d{8}\s?[A-Z])/i)?.[0];
    if (vat) return vat.replace(/\s/g, '').toUpperCase();
  }
  if (key === 'totalLabel' && /total/i.test(clean)) return 'total';
  return clean;
}

function selectedFrom(item: LayoutItem, page: number): SelectedToken {
  return { text: item.str, x: item.x, y: item.y, width: item.width, height: item.height, page };
}

export function InvoiceTemplateEditor({ invoiceId, supplierId, supplierName, onClose, onSaved }: {
  invoiceId: string;
  supplierId: string;
  supplierName: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const layoutQuery = useQuery({
    queryKey: ['invoice-layout', invoiceId],
    queryFn: () => api<Layout>(`/purchase-invoices/${invoiceId}/layout`)
  });
  const [active, setActive] = useState<FieldKey>('vat');
  const [pageIndex, setPageIndex] = useState(0);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('new');
  const [templateName, setTemplateName] = useState(`Plantilla ${new Date().toLocaleDateString('es-ES')}`);
  const [anchors, setAnchors] = useState<Partial<Record<AnchorKey, SelectedToken>>>({});
  const [headersByPage, setHeadersByPage] = useState<Record<number, Partial<Record<ColumnKey, SelectedToken>>>>({});
  const [saving, setSaving] = useState(false);
  // Rectángulo que se está dibujando: con la factura de fondo, marcar la zona es más natural que buscar el
  // texto exacto, sobre todo en cabeceras con logos o tablas.
  const [zona, setZona] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const visorRef = useRef<HTMLDivElement | null>(null);
  // Ancho disponible del visor: de él sale la resolución a pedir. Se mide el contenedor, nunca la página
  // ampliada, que produciría realimentación al cambiar el zoom.
  const [anchoVisor, setAnchoVisor] = useState(760);
  useEffect(() => {
    const nodo = visorRef.current;
    if (!nodo || typeof ResizeObserver === 'undefined') return;
    const observador = new ResizeObserver((entradas) => {
      const ancho = entradas[0]?.contentRect.width;
      if (ancho && Math.abs(ancho - anchoVisor) > 8) setAnchoVisor(ancho);
    });
    observador.observe(nodo);
    return () => observador.disconnect();
  }, [anchoVisor]);

  // Si la imagen de la factura no llega, se cae al texto del layout (que era lo que se veía antes).
  // Solo interesa si la imagen **falla**: mientras llega se ve la factura, y el texto del PDF únicamente
  // aparece como respaldo. Antes se pintaba hasta que cargaba, y quedaba por encima tapando el documento.
  const [falloImagen, setFalloImagen] = useState(false);
  // La página entera cabe en el ancho, pero a ese tamaño el texto de una factura no se lee: sin zoom no se
  // puede comprobar qué dice el documento antes de marcar la zona.
  const [zoom, setZoom] = useState(1.5);
  // Resolución del PNG: tamaño final en pantalla por la densidad del dispositivo. Pedir una resolución fija
  // muy alta y dejarla reducir el navegador es lo que dejaba el texto en gris.
  const dpr = typeof window === 'undefined' ? 1 : Math.min(window.devicePixelRatio || 1, 2);
  const anchoRender = Math.max(800, Math.min(2_600, Math.ceil(anchoVisor * zoom * dpr)));
  const [initialised, setInitialised] = useState(false);

  const templates = layoutQuery.data?.supplier.invoiceTemplates ?? [];
  const pages = layoutQuery.data?.pages ?? [];
  const page = pages[Math.min(pageIndex, Math.max(0, pages.length - 1))];
  const headers = headersByPage[page?.pageNumber ?? 1] ?? {};

  useEffect(() => {
    if (!layoutQuery.data || initialised) return;
    const preferred = layoutQuery.data.supplier.invoiceTemplates.find((template) => template.isDefault)
      ?? layoutQuery.data.supplier.invoiceTemplates[0];
    setSelectedTemplateId(preferred?.id ?? 'new');
    setInitialised(true);
  }, [layoutQuery.data, initialised]);

  useEffect(() => {
    if (!layoutQuery.data) return;
    const selected = templates.find((template) => template.id === selectedTemplateId);
    if (!selected) {
      setTemplateName(`Plantilla ${new Date().toLocaleDateString('es-ES')}`);
      setAnchors({});
      setHeadersByPage({});
      return;
    }
    setTemplateName(selected.name);
    const nextAnchors: Partial<Record<AnchorKey, SelectedToken>> = {};
    for (const [key, anchor] of Object.entries(selected.config.anchors ?? {})) {
      if (!anchor) continue;
      nextAnchors[key as AnchorKey] = { text: anchor.text, x: anchor.x, y: anchor.y, width: anchor.width ?? 0, height: 0, page: anchor.page ?? 1 };
    }
    setAnchors(nextAnchors);
    const nextHeaders: Record<number, Partial<Record<ColumnKey, SelectedToken>>> = {};
    for (const [pageNumber, config] of Object.entries(selected.config.pages ?? {})) {
      const pageHeaders: Partial<Record<ColumnKey, SelectedToken>> = {};
      for (const [key, header] of Object.entries(config.headers ?? {})) {
        if (!header) continue;
        pageHeaders[key as ColumnKey] = { text: header.text, x: header.x, y: header.y, width: header.width ?? 0, height: 0, page: Number(pageNumber) };
      }
      nextHeaders[Number(pageNumber)] = pageHeaders;
    }
    setHeadersByPage(nextHeaders);
    setPageIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplateId, layoutQuery.data]);

  const selectedFor = (key: FieldKey): SelectedToken | undefined =>
    (ANCHORS.some((anchor) => anchor.key === key) ? anchors[key as AnchorKey] : headers[key as ColumnKey]);

  const activeSelection = selectedFor(active);

  const columnsForPage = (pageNumber: number) => {
    const pageHeaders = headersByPage[pageNumber] ?? {};
    const entries = COLUMNS
      .map((column) => ({ key: column.key, token: pageHeaders[column.key] }))
      .filter((entry): entry is { key: ColumnKey; token: SelectedToken } => Boolean(entry.token))
      .map((entry) => ({ key: entry.key, center: entry.token.x + entry.token.width / 2 }))
      .sort((a, b) => a.center - b.center);
    const width = pages.find((item) => item.pageNumber === pageNumber)?.pageWidth ?? layoutQuery.data?.pageWidth ?? 595;
    const result: Partial<Record<ColumnKey, { x0: number; x1: number }>> = {};
    entries.forEach((entry, index) => {
      const previous = entries[index - 1]?.center ?? 0;
      const next = entries[index + 1]?.center ?? width;
      result[entry.key] = {
        x0: index === 0 ? 0 : (previous + entry.center) / 2,
        x1: index === entries.length - 1 ? width : (entry.center + next) / 2
      };
    });
    return result;
  };

  const currentColumns = columnsForPage(page?.pageNumber ?? 1);
  const missingAnchors = ANCHORS.filter((anchor) => !anchors[anchor.key]).map((anchor) => anchor.label);
  const missingColumns = COLUMNS.filter((column) => column.required && !currentColumns[column.key]).map((column) => column.label);
  const page1Columns = columnsForPage(1);
  const canSave = missingAnchors.length === 0 && Boolean(page1Columns.description && page1Columns.quantity && page1Columns.unitCost && page1Columns.lineTotal);

  const selectField = (key: FieldKey) => setActive(key);

  /** Punto en coordenadas de la página (las del `viewBox`), sea cual sea el zoom o el tamaño en pantalla. */
  const puntoDe = (evento: React.PointerEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const matriz = svg.getScreenCTM();
    if (!matriz) return null;
    const punto = new DOMPoint(evento.clientX, evento.clientY).matrixTransform(matriz.inverse());
    return { x: punto.x, y: punto.y };
  };

  const empezarZona = (evento: React.PointerEvent<SVGSVGElement>) => {
    // Solo el botón principal y un dedo: si no, el botón derecho o un segundo dedo abren una zona que se
    // queda pegada cuando no llega el `pointerup`.
    if (evento.button !== 0 || !evento.isPrimary) return;
    const punto = puntoDe(evento);
    if (!punto) return;
    // **Sin** `setPointerCapture`: con la captura, el `click` se queda en el `<svg>` y el token de debajo
    // nunca lo recibe. Por eso el clic se resuelve también al soltar.

    setZona({ x0: punto.x, y0: punto.y, x1: punto.x, y1: punto.y });
  };

  const moverZona = (evento: React.PointerEvent<SVGSVGElement>) => {
    if (!zona) return;
    const punto = puntoDe(evento);
    if (!punto) return;
    setZona({ ...zona, x1: punto.x, y1: punto.y });
  };

  /**
   * Al soltar: se queda con el texto que cae **dentro** del rectángulo. Si hay varios, el más cercano al
   * punto donde empezó el arrastre, que es donde el usuario puso el ojo.
   */
  const terminarZona = (evento: React.PointerEvent<SVGSVGElement>) => {
    if (!zona || !page) { setZona(null); return; }
    const ancho = Math.abs(zona.x1 - zona.x0);
    const alto = Math.abs(zona.y1 - zona.y0);
    const izquierda = Math.min(zona.x0, zona.x1);
    const arriba = Math.min(zona.y0, zona.y1);
    setZona(null);
    // Un clic sin arrastre: la palabra **cuya caja contiene el punto**. Antes se comparaba con el centro, así
    // que un clic en el borde de una palabra corta no seleccionaba nada (y parecía que no funcionaba).
    const cajaDe = (item: LayoutItem) => {
      const altoItem = Math.max(item.height, 6);
      const anchoItem = Math.max(item.width, 6);
      const top = page.pageHeight - item.y - item.height;
      return { x: item.x, y: top, w: anchoItem, h: altoItem };
    };
    if (ancho < page.pageWidth * 0.004 && alto < page.pageHeight * 0.004) {
      const dentro = page.items.find((item) => {
        const caja = cajaDe(item);
        return zona.x0 >= caja.x - 2 && zona.x0 <= caja.x + caja.w + 2
          && zona.y0 >= caja.y - 2 && zona.y0 <= caja.y + caja.h + 2;
      });
      // Si el clic cae entre palabras (espacios, márgenes), la más cercana a menos de 12 puntos.
      const elegido = dentro ?? page.items
        .map((item) => ({ item, caja: cajaDe(item) }))
        .map(({ item, caja }) => ({ item, distancia: Math.hypot(caja.x + caja.w / 2 - zona.x0, caja.y + caja.h / 2 - zona.y0) }))
        .filter(({ distancia }) => distancia < 12)
        .sort((a, b) => a.distancia - b.distancia)[0]?.item;
      if (elegido) pickToken(elegido);
      return;
    }

    // Se queda con la palabra que **más superficie comparte** con el rectángulo. Con el centro, una caja
    // ajustada sobre media palabra no seleccionaba nada, que es lo que pasaba al marcar de cerca.
    const solape = page.items
      .map((item) => {
        const caja = cajaDe(item);
        const anchoSolape = Math.max(0, Math.min(caja.x + caja.w, izquierda + ancho) - Math.max(caja.x, izquierda));
        const altoSolape = Math.max(0, Math.min(caja.y + caja.h, arriba + alto) - Math.max(caja.y, arriba));
        return { item, area: anchoSolape * altoSolape };
      })
      .filter(({ area }) => area > 0)
      .sort((a, b) => b.area - a.area);
    if (solape.length === 0) {
      // La zona no tiene texto: puede ser una cabecera rasterizada. Se guarda el rectángulo tal cual, que es
      // lo que el usuario ha marcado; sin esto, marcar sobre una imagen no hacía nada.
      const sintetico: SelectedToken = {
        text: '',
        x: izquierda,
        y: page.pageHeight - (arriba + alto),
        width: ancho,
        height: alto,
        page: page.pageNumber
      };
      aplicarToken(sintetico);
      return;
    }

    // Cabecera de la tabla: se puede marcar **toda la banda**, no una sola palabra. Se guarda el rectángulo
    // que la cubre y los textos que contenga.
    if (active === 'tableHeader' && solape.length > 1) {
      const textos = solape.map(({ item }) => item.str.trim()).filter(Boolean);
      setHeadersByPage((current) => ({
        ...current,
        [page.pageNumber]: {
          ...(current[page.pageNumber] ?? {}),
          tableHeader: {
            text: textos.join(' '),
            x: izquierda,
            y: page.pageHeight - (arriba + alto),
            width: ancho,
            height: alto,
            page: page.pageNumber
          }
        }
      }));
      return;
    }

    pickToken(solape[0]!.item);
    void evento;
  };

  const aplicarToken = (token: SelectedToken) => {
    if (ANCHORS.some((anchor) => anchor.key === active)) {
      setAnchors((current) => ({ ...current, [active as AnchorKey]: token }));
    } else {
      setHeadersByPage((current) => ({
        ...current,
        [token.page]: { ...(current[token.page] ?? {}), [active as ColumnKey]: token }
      }));
    }
    const index = FIELD_ORDER.indexOf(active);
    setActive(FIELD_ORDER[Math.min(FIELD_ORDER.length - 1, index + 1)]!);
  };

  const pickToken = (item: LayoutItem) => {
    if (!page) return;
    const token = selectedFrom(item, page.pageNumber);
    if (ANCHORS.some((anchor) => anchor.key === active)) {
      setAnchors((current) => ({ ...current, [active as AnchorKey]: token }));
    } else {
      setHeadersByPage((current) => ({
        ...current,
        [page.pageNumber]: { ...(current[page.pageNumber] ?? {}), [active as ColumnKey]: token }
      }));
    }
    const index = FIELD_ORDER.indexOf(active);
    setActive(FIELD_ORDER[Math.min(FIELD_ORDER.length - 1, index + 1)]!);
  };

  const save = async () => {
    const firstPage = pages[0];
    if (!firstPage || !canSave) {
      toast.error('Marca los cuatro puntos y, al menos en la primera página, descripción, cantidad, precio e importe');
      return;
    }
    setSaving(true);
    try {
      const pageConfigs: Record<string, unknown> = {};
      for (const layoutPage of pages) {
        const pageHeaders = headersByPage[layoutPage.pageNumber] ?? {};
        if (!Object.keys(pageHeaders).length) continue;
        const columns = columnsForPage(layoutPage.pageNumber);
        pageConfigs[String(layoutPage.pageNumber)] = {
          columns,
          headerTokens: COLUMNS.filter((column) => pageHeaders[column.key]).map((column) => pageHeaders[column.key]!.text),
          headers: Object.fromEntries(COLUMNS.filter((column) => pageHeaders[column.key]).map((column) => {
            const token = pageHeaders[column.key]!;
            return [column.key, { text: token.text, match: anchorMatch('vat', token.text), x: token.x, y: token.y, width: token.width, height: token.height, page: layoutPage.pageNumber }];
          }))
        };
      }
      const payload = {
        name: templateName.trim() || 'Principal',
        config: {
          version: 2,
          pageWidth: firstPage.pageWidth,
          columns: page1Columns,
          anchors: Object.fromEntries(ANCHORS.map(({ key }) => {
            const token = anchors[key]!;
            return [key, { text: token.text, match: anchorMatch(key, token.text), x: token.x, y: token.y, width: token.width, height: token.height, page: token.page ?? 1 }];
          })),
          headerTokens: COLUMNS.filter((column) => headersByPage[1]?.[column.key]).map((column) => headersByPage[1]![column.key]!.text),
          pages: pageConfigs
        }
      };
      if (selectedTemplateId !== 'new') {
        await api(`/suppliers/${supplierId}/invoice-templates/${selectedTemplateId}`, { method: 'PUT', body: payload });
      } else {
        await api(`/suppliers/${supplierId}/invoice-templates`, { method: 'POST', body: payload });
      }
      toast.success('Plantilla visual guardada para este proveedor');
      onSaved();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const pageOptions = useMemo(() => pages.map((item) => item.pageNumber), [pages]);

  if (layoutQuery.error) return <ErrorState message={(layoutQuery.error as Error).message} onRetry={() => void layoutQuery.refetch()} />;
  if (layoutQuery.isLoading || !layoutQuery.data || !page) return <div className="h-72 animate-pulse rounded-lg bg-muted" />;
  const layout = layoutQuery.data;

  return (
    <div>
      <div className="mb-3">
        <button type="button" onClick={onClose} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"><ArrowLeft className="size-3" /> Volver a la factura</button>
      </div>
      <PageHeader
        title="Editor visual de plantilla"
        description={`${supplierName} · factura ${layout.invoiceNumber}. Marca los cuatro puntos del diccionario y las columnas de cada página.`}
        actions={<Button loading={saving} disabled={!canSave} onClick={() => void save()}><Save /> Guardar plantilla</Button>}
      />

      <div className="grid gap-4 xl:grid-cols-[1fr_370px]">
        <Card className="overflow-hidden">
          <CardHeader
            title={`Factura · página ${page.pageNumber} de ${layout.totalPages}`}
            description="Haz clic sobre el valor del campo activo o arrastra un rectángulo encima del documento. Mientras la imagen se dibuja se muestra el texto del PDF."
            actions={pageOptions.length > 1 ? (
              <div className="flex items-center gap-1">
                {pageOptions.map((number) => (
                  <Button key={number} size="sm" variant={page.pageNumber === number ? 'default' : 'outline'} onClick={() => setPageIndex(number - 1)}>{number}</Button>
                ))}
              </div>
            ) : null}
          />
          <CardContent className="bg-muted/30">
            <div className="mx-auto max-h-[680px] max-w-[760px] overflow-auto rounded border border-border bg-white p-2 shadow-inner">
              <div className="mb-2 flex items-center justify-end gap-1 text-xs">
                <a
                  className="mr-2 underline decoration-dotted hover:text-foreground"
                  href={`/api/purchase-invoices/${invoiceId}/document.pdf`}
                  target="_blank" rel="noreferrer"
                >
                  Abrir el PDF original
                </a>
                <span className="mr-1 text-muted-foreground">Zoom</span>
                {[1, 1.5, 2, 3].map((valor) => (
                  <Button
                    key={valor}
                    size="sm"
                    variant={zoom === valor ? 'default' : 'outline'}
                    onClick={() => setZoom(valor)}
                  >
                    {Math.round(valor * 100)} %
                  </Button>
                ))}
              </div>
              <div ref={visorRef} className="mx-auto w-full">
              <div
                className="relative mx-auto bg-white shadow-inner"
                style={{ width: `${zoom * 100}%`, aspectRatio: `${page.pageWidth} / ${page.pageHeight}` }}
              >
                {/* La imagen va en HTML y las zonas en un SVG transparente encima: separadas se inspecciona
                    mejor y el navegador no tiene que filtrar la foto dentro del SVG. */}
                <img
                  src={`/api/purchase-invoices/${invoiceId}/pages/${page.pageNumber}.png?ancho=${anchoRender}`}
                  alt={`Factura, página ${page.pageNumber}`}
                  draggable={false}
                  onError={() => setFalloImagen(true)}
                  className="pointer-events-none absolute inset-0 h-full w-full select-none"
                />
              <svg
                ref={svgRef}
                viewBox={`0 0 ${page.pageWidth} ${page.pageHeight}`}
                preserveAspectRatio="xMinYMin meet"
                className="absolute inset-0 h-full w-full touch-none"
                role="img"
                aria-label={`Factura, página ${page.pageNumber}`}
                onPointerDown={empezarZona}
                onPointerMove={moverZona}
                onPointerUp={terminarZona}
                onPointerCancel={() => setZona(null)}
              >
                {falloImagen ? <rect x={0} y={0} width={page.pageWidth} height={page.pageHeight} fill="#ffffff" /> : null}
                {page.items.map((item, index) => {
                  const selected = [...Object.values(anchors), ...Object.values(headersByPage[page.pageNumber] ?? {})].some((token) => token && token.page === page.pageNumber && token.text === item.str && token.x === item.x && token.y === item.y);
                  const top = page.pageHeight - item.y - item.height;
                  const width = Math.max(item.width, item.str.length * item.height * 0.5, 4);
                  return (
                    <g key={`${index}-${item.x}-${item.y}`} className="cursor-pointer">
                      <rect x={item.x} y={top} width={width} height={Math.max(item.height, 6)} fill={selected ? '#2563eb22' : 'transparent'} stroke={selected ? '#2563eb' : 'transparent'} strokeWidth={0.7} />
                      {/* Con la factura de fondo, pintar el texto otra vez lo duplicaría: solo se dibuja si
                          la imagen no ha cargado. */}
                      {falloImagen ? (
                        <text x={item.x} y={top + item.height * 0.82} fontSize={Math.max(4.5, item.height)} fill="#0f172a" style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}>{item.str}</text>
                      ) : null}
                    </g>
                  );
                })}
                {/* Localizaciones ya marcadas, pintadas sobre la factura: el emisor no siempre está en la
                    cabecera, así que hay que **ver** dónde se ha aprendido cada dato. */}
                {[...ANCHORS, ...COLUMNS].map((campo) => {
                  const esAncla = ANCHORS.some((ancla) => ancla.key === campo.key);
                  const token = esAncla ? anchors[campo.key as AnchorKey] : headers[campo.key as ColumnKey];
                  if (!token || token.page !== page.pageNumber) return null;
                  const alto = Math.max(token.height, page.pageHeight * 0.012);
                  const top = page.pageHeight - token.y - alto;
                  const activo = active === campo.key;
                  return (
                    <g key={`zona-${campo.key}`} pointerEvents="none">
                      <rect
                        x={token.x - 1} y={top - 1}
                        width={Math.max(token.width, 30) + 2} height={alto + 2}
                        fill={activo ? '#2563eb22' : '#0f172a11'}
                        stroke={activo ? '#2563eb' : '#64748b'} strokeWidth={0.8}
                        strokeDasharray={activo ? undefined : '3 2'}
                      />
                      <text x={token.x} y={top - 3} fontSize={Math.max(6, page.pageHeight * 0.011)} fill={activo ? '#2563eb' : '#475569'}>
                        {campo.label}
                      </text>
                    </g>
                  );
                })}
                {zona ? (
                  <rect
                    x={Math.min(zona.x0, zona.x1)}
                    y={Math.min(zona.y0, zona.y1)}
                    width={Math.abs(zona.x1 - zona.x0)}
                    height={Math.abs(zona.y1 - zona.y0)}
                    fill="#2563eb22" stroke="#2563eb" strokeWidth={1.2} strokeDasharray="4 3"
                  />
                ) : null}
              </svg>
              </div>
              </div>
              <p className="mt-2 text-center text-xs text-muted-foreground">
                Marca el campo activo con un <strong>clic sobre la palabra</strong> o <strong>arrastrando un rectángulo</strong> por encima del documento.
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader title="Plantilla" description="Puedes guardar varias maquetas para el mismo proveedor." />
            <CardContent className="space-y-2">
              <Field label="Plantilla existente">
                <Select value={selectedTemplateId} onChange={(event) => setSelectedTemplateId(event.target.value)}>
                  <option value="new">Nueva plantilla</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>{template.name}{template.isDefault ? ' · predeterminada' : ''}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Nombre de la plantilla"><Input value={templateName} onChange={(event) => setTemplateName(event.target.value)} maxLength={120} /></Field>
            </CardContent>
          </Card>

          <Card>
            <CardHeader title="Diccionario · 4 puntos" description="Referencias estables para identificar proveedor y plantilla." />
            <CardContent className="space-y-2">
              {ANCHORS.map((anchor) => {
                const token = anchors[anchor.key];
                return (
                  <button type="button" key={anchor.key} onClick={() => selectField(anchor.key)} className={`w-full rounded-md border p-2 text-left text-xs transition ${active === anchor.key ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted'}`}>
                    <p className="font-medium">{anchor.label}</p>
                    <p className="mt-0.5 truncate text-[10px] text-muted-foreground">{token ? `Pág. ${token.page ?? 1} · ${token.text}` : anchor.hint}</p>
                  </button>
                );
              })}
            </CardContent>
          </Card>

          <Card>
            <CardHeader title={`Columnas · página ${page.pageNumber}`} description="Marca las cabeceras de esta página. Si la tabla continúa, cambia de página y repite." />
            <CardContent className="space-y-2">
              {COLUMNS.map((column) => {
                const token = headers[column.key];
                return (
                  <button type="button" key={column.key} onClick={() => selectField(column.key)} className={`w-full rounded-md border p-2 text-left text-xs transition ${active === column.key ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted'}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">{column.label}</span>
                      {token ? <CheckCircle2 className="size-3.5 text-emerald-600" /> : column.required ? <Badge tone="warning">Obligatoria</Badge> : <Badge tone="neutral">Opcional</Badge>}
                    </div>
                    <p className="mt-0.5 truncate text-[10px] text-muted-foreground">{token ? token.text : 'Sin marcar'}</p>
                  </button>
                );
              })}
            </CardContent>
          </Card>

          <div className="rounded-lg border border-border bg-muted/20 p-3 text-xs">
            <p className="flex items-center gap-2 font-medium"><MousePointerClick className="size-4 text-primary" /> Campo activo: {ANCHORS.find((a) => a.key === active)?.label ?? COLUMNS.find((c) => c.key === active)?.label}</p>
            <p className="mt-1 text-muted-foreground">{activeSelection ? `Seleccionado: página ${activeSelection.page ?? 1} · ${activeSelection.text}` : 'Haz clic en la vista del PDF para marcarlo.'}</p>
            {!canSave ? <p className="mt-2 text-amber-600">Faltan: {[...missingAnchors, ...(page?.pageNumber === 1 ? missingColumns : [])].join(' · ') || 'columnas de la primera página'}</p> : <p className="mt-2 text-emerald-600">Listo para guardar.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
